import { difference, uniq } from 'lodash-es';

import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';

import { MwUserAccessService } from '@core/auth';
import { NavBarItem } from '@core/config';

import { MwLayoutService } from '../../layout.service';
import { MenuItemHeight } from './nav-list.constants';
import { flatNavBarItems } from './nav-list.helpers';
import { MwNavListService } from './nav-list.service';

@Injectable()
export class NavListDataService implements OnDestroy {
  get items$(): Observable<Readonly<NavBarItem[]>> {
    return this._items$.asObservable();
  }

  get activatedItem(): NavBarItem {
    return this._activatedItem;
  }

  heightReduced = new EventEmitter<number>();

  private _items$: BehaviorSubject<Readonly<NavBarItem[]>> =
    new BehaviorSubject<Readonly<NavBarItem[]>>([]);
  private _activatedItem: NavBarItem = null;
  private _activeItems: NavBarItem[] = [];
  private _openedItems: NavBarItem[] = [];
  private _flatItems: NavBarItem[] = [];
  private _unsubscribeAll$ = new Subject<void>();
  private _isReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly layoutService: MwLayoutService,
    private readonly userAccessService: MwUserAccessService,
    private readonly service: MwNavListService,
    private readonly router: Router
  ) {
    this.subscribeToNavBarItemsChange();
    this.subscribeToNavigationEnd();
  }

  setupActiveItemByRoute(): void {
    const route = this.router.url;
    const routeUrlSplitted = (
      route.startsWith('/pages/') ? route.replace('/pages/', '') : route
    ).split('/');
    const routeUrlStartSegment =
      routeUrlSplitted?.length && routeUrlSplitted[0]
        ? routeUrlSplitted[0]
        : route;
    const activeItem =
      this._flatItems.find((x) => x.route && route.endsWith(x.route)) ||
      this._flatItems.find(
        (x) => x.route && routeUrlStartSegment.startsWith(x.route)
      ) ||
      null;

    this._flatItems.forEach((item) => {
      item.isOpened = false;
      item.isActive = false;
    });
    
    if (activeItem) {
      this.setActiveItem(activeItem);
      this.toggleItem(activeItem);
      this._items$.next([...this._items$.value]);
    }
  }

  toggleItem(item: NavBarItem): void {
    const isOpen = !item.isOpened;
    const lastOpenedItems = [...this._openedItems];
    this._openedItems.forEach((item) => (item.isOpened = false));
    this._openedItems = isOpen ? [item] : [];

    item.isOpened = isOpen;

    let parent = item.parent;
    while (parent) {
      this._openedItems.push(parent);
      parent.isOpened = true;
      parent = parent.parent;
    }

    this.recalculateHeights(lastOpenedItems);
  }

  setActiveItem(item: NavBarItem): void {
    if (!item) {
      return;
    }

    if (!item.hasChildren && this._activeItems.indexOf(item) === -1) {
      this._activeItems.forEach((item) => (item.isActive = false));
      this._activeItems = [item];
      this._activatedItem = item;
      item.isActive = true;

      if (item.parent) {
        let parent = item.parent;
        while (parent) {
          this._activeItems.push(parent);
          parent.isActive = true;
          parent = parent.parent;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  closeOpenedItems(): void {
    this._openedItems.forEach((item) => (item.isOpened = false));
    this._openedItems = [];
  }

  private removeRestrictedMenuItems(
    rootItems: NavBarItem[]
  ): Observable<NavBarItem[]> {
    const menuAccessActions = this.getMenuAccessActions(rootItems);

    if (!menuAccessActions.length) {
      return of(rootItems);
    }

    return this.userAccessService.hasAccessBatch(menuAccessActions).pipe(
      map((accessActions) => {
        const filterItems = (items: NavBarItem[]): NavBarItem[] => {
          const result = items.filter(
            (item) => accessActions[item.action] !== false
          );

          result.forEach(
            (item) => (item.children = filterItems(item.children))
          );

          return result.filter(
            (item) => item.children?.length > 0 || item.route || item.showAlways
          );
        };

        return filterItems(rootItems);
      })
    );
  }

  private setupNavBarItems(items: NavBarItem[]): void {
    this.removeRestrictedMenuItems(items).subscribe((items) => {
      this._items$.next(items);
      this._flatItems = flatNavBarItems(items);

      this.service.loadIcons(items);
      this.layoutService.setNavBarItems(items);
      this._isReady$.next(true);
    });
  }

  private subscribeToNavBarItemsChange(): void {
    this.layoutService.changeNavBarItems$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((items) => this.setupNavBarItems(items));
  }

  private subscribeToNavigationEnd(): void {
    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => true),
      startWith(true)
    );

    combineLatest([navigationEnd$, this._isReady$])
      .pipe(
        takeUntil(this._unsubscribeAll$),
        filter(([isNavigationEnd, isReady]) => isNavigationEnd && isReady)
      )
      .subscribe(() => this.setupActiveItemByRoute());
  }

  private recalculateHeights(lastOpenedItems: NavBarItem[]): void {
    const diff = (
      difference(lastOpenedItems, this._openedItems) as NavBarItem[]
    ).filter((t) => t.children);

    if (diff.length > 0) {
      const size =
        diff
          .map((t) => t.children.length)
          .reduce((prevVal, curVal) => prevVal + curVal) * MenuItemHeight;

      this.heightReduced.emit(size);
    }
  }

  private getMenuAccessActions(rootItems: NavBarItem[]): string[] {
    const getActions = (items: NavBarItem[]): string[] => {
      const actions = items.filter((t) => t.action).map((t) => t.action);

      const childrenActions = items
        .filter((t) => t.children?.length > 0)
        .reduce((prev, curr) => [...prev, ...getActions(curr.children)], []);

      return uniq([...actions, ...childrenActions]);
    };

    return getActions(rootItems);
  }
}
